import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../utils/apiClient";
import { Spinner, Alert, Table } from "react-bootstrap";
import { 
  Plus, Layers, Users, Clock, Calendar, MoreVertical, MoreHorizontal,
  Eye, Edit, Trash2, Search, LayoutGrid, List, SlidersHorizontal, Star
} from "lucide-react";
import { getCurrentUser } from "../../utils/auth";
import { StatusBadge, EmptyState, PageHeader, ProgressBar, AppCard, AppCardHeader, AppCardContent, ConfirmModal, AlertModal, AppButton, ActionMenu, ActionMenuItem, ActionMenuDivider } from "../ui";
import { FilterModal, FilterGroup } from "../ui/FilterModal";
import CommonToaster from "../Common/CommonToaster";
import OverLimitBanner from "../Common/OverLimitBanner";
import { usePlanUsage, isOverLimit, isStrictlyOverLimit } from "../../hooks/usePlanUsage";
import { useBranch } from "../../contexts/BranchContext";
import "./Batches.scss";

const DEFAULT_PAGE_SIZE = 12;

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-IN", {
    day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata",
  });
};

const calculateProgress = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  const now = Date.now();
  
  if (now < start) return 0;
  if (now > end) return 100;
  
  return Math.round(((now - start) / (end - start)) * 100);
};


function RatingDisplay({ rating, label }) {
  if (!rating) return <span className="batch-rating__na">N/A</span>;
  return (
    <span className="batch-rating__value">
      <Star size={13} className="batch-rating__star" />
      {rating.toFixed(1)}
      {label && <span className="batch-rating__label">{label}</span>}
    </span>
  );
}

function BatchCard({ batch, onDelete, onEdit, isAdmin }) {
  const navigate = useNavigate();
  const progress = calculateProgress(batch.startDate, batch.endDate);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  return (
  <>
    <AppCard className="h-100 hover-card">
      <AppCardHeader className="batch-card-header">
        <div 
          className="d-flex align-items-center gap-3" 
          style={{ cursor: 'pointer', minWidth: 0, flex: 1 }}
          onClick={() => navigate(`/batches/${batch._id}`)}
        >
          <div className="batch-icon-box d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
            <Layers size={20} />
          </div>
          <div style={{ minWidth: 0 }}>
            <h6 className="mb-0 fw-semibold batch-card__title">{batch.name}</h6>
            <p className="batch-card__subtitle mb-0">{batch.course?.name || "-"}</p>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2 mt-2" style={{ flexShrink: 0 }}>
          <StatusBadge status={batch.status} />
          <ActionMenu trigger={<MoreVertical size={16} />}>
            <ActionMenuItem onClick={() => navigate(`/batches/${batch._id}`)}><Eye size={14} className="me-2" />View Details</ActionMenuItem>
            <ActionMenuItem onClick={() => onEdit(batch._id)}><Edit size={14} className="me-2" />Edit</ActionMenuItem>
            <ActionMenuDivider />
            <ActionMenuItem danger onClick={() => setShowDeleteConfirm(true)}><Trash2 size={14} className="me-2" />Delete</ActionMenuItem>
          </ActionMenu>
        </div>
      </AppCardHeader>
      <AppCardContent className="batch-card-body">
        <div className="d-flex flex-column gap-3">
          <div className="row g-2">
            <div className="col-6">
              <div className="d-flex align-items-center gap-2 batch-card__meta">
                <Users size={14} />
                <span>{batch.studentCount ?? batch.strength ?? 0}{batch.effectiveMaxStudents ? `/${batch.effectiveMaxStudents}` : ''} students</span>
              </div>
            </div>
            <div className="col-6">
              <div className="d-flex align-items-center gap-2 batch-card__meta">
                <Clock size={14} />
                <span>{batch.startTime || "-"}</span>
              </div>
            </div>
          </div>

          <div className="batch-card__meta">
            <span>{batch.batchType === "WEEKEND" ? "Sat-Sun" : "Mon-Fri"}</span>
          </div>

          <div>
            <div className="d-flex justify-content-between mb-1 batch-card__meta">
              <span>Progress</span>
              <span className="fw-medium" style={{ color: 'var(--text-primary)' }}>{progress}%</span>
            </div>
            <ProgressBar value={progress} />
          </div>

          {isAdmin && batch.feedbackCount > 0 && (
            <div className="d-flex justify-content-between batch-card__ratings">
              <div className="d-flex align-items-center gap-2 batch-card__meta">
                <span>Course:</span>
                <RatingDisplay rating={batch.avgBatchRating} />
              </div>
              <div className="d-flex align-items-center gap-2 batch-card__meta">
                <span>Trainer:</span>
                <RatingDisplay rating={batch.avgTrainerRating} />
              </div>
            </div>
          )}

          <div className="d-flex justify-content-between pt-2 border-top batch-card__date">
            <div className="d-flex align-items-center gap-1">
              <Calendar size={12} />
              <span>{formatDate(batch.startDate)}</span>
            </div>
            <span>to</span>
            <div className="d-flex align-items-center gap-1">
              <Calendar size={12} />
              <span>{formatDate(batch.endDate)}</span>
            </div>
          </div>
        </div>
      </AppCardContent>
    </AppCard>

    <ConfirmModal
      show={showDeleteConfirm}
      onHide={() => setShowDeleteConfirm(false)}
      onConfirm={() => { setShowDeleteConfirm(false); onDelete(batch._id); }}
      title="Delete Batch"
      message={`Are you sure you want to delete batch "${batch.name}"?`}
      confirmText="Delete"
      variant="danger"
    />
  </>
  );
}

function BatchesTable({ batches, onDelete, onEdit, isAdmin }) {
  const navigate = useNavigate();
  
  return (
    <AppCard>
      <AppCardContent className="p-0">
        <div className="table-responsive">
          <Table className="mb-0 batch-table" hover>
            <thead>
              <tr>
                <th style={{ width: 200 }}>Batch</th>
                <th>Course</th>
                <th>Instructor</th>
                <th>Schedule</th>
                <th className="text-center">Students</th>
                <th>Duration</th>
                {isAdmin && <th className="text-center">Course Rating</th>}
                {isAdmin && <th className="text-center">Trainer Rating</th>}
                <th>Status</th>
                <th style={{ width: 50 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((batch) => (
                <tr key={batch._id}>
                  <td>
                    <div 
                      className="d-flex align-items-center gap-3"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/batches/${batch._id}`)}
                    >
                      <div className="batch-icon-box d-flex align-items-center justify-content-center" style={{ width: 36, height: 36 }}>
                        <Layers size={16} />
                      </div>
                      <span className="fw-medium batch-card__title">{batch.name}</span>
                    </div>
                  </td>
                  <td>{batch.course?.name || "-"}</td>
                  <td>{batch.instructor?.name || "-"}</td>
                  <td>
                    <div style={{ fontSize: '0.875rem' }}>
                      {batch.startTime && batch.endTime 
                        ? `${batch.startTime} - ${batch.endTime}` 
                        : batch.startTime || "-"}
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                      {batch.batchType === "WEEKEND" ? "Sat-Sun" : "Mon-Fri"}
                    </div>
                  </td>
                  <td className="text-center">{batch.studentCount ?? batch.strength ?? 0}{batch.effectiveMaxStudents ? `/${batch.effectiveMaxStudents}` : ''}</td>
                  <td style={{ fontSize: '0.875rem' }}>
                    {formatDate(batch.startDate)} - {formatDate(batch.endDate)}
                  </td>
                  {isAdmin && (
                    <td className="text-center">
                      <RatingDisplay rating={batch.avgBatchRating} />
                    </td>
                  )}
                  {isAdmin && (
                    <td className="text-center">
                      <RatingDisplay rating={batch.avgTrainerRating} />
                    </td>
                  )}
                  <td>
                    <StatusBadge status={batch.status} />
                  </td>
                  <td>
                    <ActionMenu trigger={<MoreHorizontal size={16} />}>
                      <ActionMenuItem onClick={() => navigate(`/batches/${batch._id}`)}><Eye size={14} className="me-2" />View Details</ActionMenuItem>
                      <ActionMenuItem onClick={() => onEdit(batch._id)}><Edit size={14} className="me-2" />Edit</ActionMenuItem>
                      <ActionMenuDivider />
                      <ActionMenuItem danger onClick={() => onDelete(batch._id)}><Trash2 size={14} className="me-2" />Delete</ActionMenuItem>
                    </ActionMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </AppCardContent>
    </AppCard>
  );
}

const BatchesPage = () => {
  const [view, setView] = useState("grid");
  const [filters, setFilters] = useState({ course: "", status: "", search: "" });
  const [batches, setBatches] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [alertModal, setAlertModal] = useState({ show: false, title: '', message: '' });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);

  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const isAdmin = ["admin", "superadmin"].includes(currentUser?.usertype);

  // Plan usage limits
  const { usage } = usePlanUsage();
  const batchLimit = isOverLimit(usage?.batches);
  const batchStrictly = isStrictlyOverLimit(usage?.batches);
  const { selectedBranchId } = useBranch();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setErr("");
      try {
        const params = { page, pageSize };
        if (filters.course) params.course = filters.course;
        if (filters.status) params.status = filters.status;
        if (filters.search) params.search = filters.search;

        const { data } = await apiClient.get("/batches/list", { params });
        setBatches(data.batches || []);
        setTotal(data.total || 0);

        const uniqueCourses = [];
        const courseIdSet = new Set();
        (data.batches || []).forEach(b => {
          if (b.course && b.course._id && !courseIdSet.has(b.course._id)) {
            courseIdSet.add(b.course._id);
            uniqueCourses.push({ value: b.course._id, label: b.course.name });
          }
        });
        setCourseOptions(uniqueCourses);
      } catch (e) {
        setErr("Failed to load batches.");
        setToast({ show: true, message: "Failed to load batches.", type: "error" });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filters, page, pageSize, selectedBranchId]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const handleDeleteBatch = async (batchId) => {
    setLoading(true);
    try {
      await apiClient.delete(`/batches/${batchId}`);
      setToast({ show: true, message: "Batch deleted successfully!", type: "success" });
      setBatches((prev) => prev.filter((b) => b._id !== batchId));
      setTotal((prev) => prev - 1);
    } catch (e) {
      if(e.response?.data?.errorCode === 'BATCH_HAS_STUDENTS') {
        setAlertModal({ show: true, title: 'Cannot Delete Batch', message: e.response.data.message });
      } else {
        setToast({ show: true, message: "Failed to delete batch.", type: "error" });
      }
    }
    setLoading(false);
  };

  const handleEditBatch = (batchId) => {
    navigate(`/batches/edit/${batchId}`);
  };

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "Upcoming", label: "Upcoming" },
    { value: "Ongoing", label: "Ongoing" },
    { value: "Completed", label: "Completed" },
  ];

  const [showFilterModal, setShowFilterModal] = useState(false);
  const activeFilterCount = (filters.status ? 1 : 0) + (filters.course ? 1 : 0);

  const handleResetFilters = () => {
    setFilters(f => ({ ...f, status: '', course: '' }));
  };

  return (
    <div className="batches-page-modern">
      <OverLimitBanner
        resourceName="Batches"
        used={batchLimit.used}
        max={batchLimit.max}
        overLimit={batchLimit.overLimit}
        strictlyOver={batchStrictly.overLimit}
      />
      <PageHeader
        icon={Layers}
        title="Batches"
        description={`Manage ${total} batches across all courses`}
        actions={
          <AppButton
            onClick={() => navigate("/batches/add")}
            disabled={batchLimit.overLimit}
            title={batchLimit.overLimit ? `Plan limit reached (${batchLimit.used}/${batchLimit.max})` : ''}
          >
            <Plus size={16} /> Add Batch
          </AppButton>
        }
      />

      {/* Toolbar: Search + Filters + View Toggle */}
      <div className="bt-toolbar">
        <div className="bt-search">
          <Search size={16} className="bt-search-icon" />
          <input
            type="text"
            className="bt-search-input"
            placeholder="Search batches..."
            value={filters.search}
            onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
          />
        </div>

        <div className="bt-toolbar-actions">
          {/* Desktop inline filters */}
          <select
            className="bt-filter-select d-none d-md-block"
            value={filters.status}
            onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
          >
            {statusOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {courseOptions.length > 0 && (
            <select
              className="bt-filter-select d-none d-md-block"
              value={filters.course}
              onChange={(e) => setFilters(f => ({ ...f, course: e.target.value }))}
            >
              <option value="">All Courses</option>
              {courseOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          )}

          {/* Mobile filter button */}
          <button
            className={`bt-filter-btn d-md-none${activeFilterCount > 0 ? ' bt-filter-btn--active' : ''}`}
            onClick={() => setShowFilterModal(true)}
            aria-label="Open filters"
          >
            <SlidersHorizontal size={16} />
            {activeFilterCount > 0 && (
              <span className="bt-filter-badge">{activeFilterCount}</span>
            )}
          </button>

          <div className="bt-view-toggle">
            <button
              className={`bt-view-btn ${view === "grid" ? "active" : ""}`}
              onClick={() => setView("grid")}
              aria-label="Grid view"
            >
              <LayoutGrid size={16} />
            </button>
            <button
              className={`bt-view-btn ${view === "list" ? "active" : ""}`}
              onClick={() => setView("list")}
              aria-label="List view"
            >
              <List size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center my-5">
          <Spinner animation="border" variant="secondary" />
        </div>
      )}

      {/* Error State */}
      {err && !loading && (
        <Alert variant="danger" className="my-5 text-center">
          {err}
        </Alert>
      )}

      {/* Empty State */}
      {!loading && !err && batches.length === 0 && (
        <EmptyState
          icon={<Layers />}
          title="No batches found"
          description="Try adjusting your search or filter criteria"
        />
      )}

      {/* Grid View */}
      {!loading && !err && batches.length > 0 && view === "grid" && (
        <div className="row g-4">
          {batches.map((batch) => (
            <div key={batch._id} className="col-sm-6 col-lg-4">
              <BatchCard 
                batch={batch} 
                onDelete={handleDeleteBatch}
                onEdit={handleEditBatch}
                isAdmin={isAdmin}
              />
            </div>
          ))}
        </div>
      )}

      {/* List View */}
      {!loading && !err && batches.length > 0 && view === "list" && (
        <BatchesTable 
          batches={batches} 
          onDelete={handleDeleteBatch}
          onEdit={handleEditBatch}
          isAdmin={isAdmin}
        />
      )}

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      <AlertModal
        show={alertModal.show}
        onClose={() => setAlertModal({ show: false, title: '', message: '' })}
        title={alertModal.title}
        message={alertModal.message}
        variant="danger"
      />
      <FilterModal
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        onReset={handleResetFilters}
      >
        <FilterGroup label="Status">
          <select
            value={filters.status}
            onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
          >
            {statusOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </FilterGroup>
        {courseOptions.length > 0 && (
          <FilterGroup label="Course">
            <select
              value={filters.course}
              onChange={(e) => setFilters(f => ({ ...f, course: e.target.value }))}
            >
              <option value="">All Courses</option>
              {courseOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </FilterGroup>
        )}
      </FilterModal>
    </div>
  );
};

export default BatchesPage;
